/**
 * Reusable list surface for every table screen in the app.
 *
 * Ships the states a list is not finished without: sortable headers, a debounced
 * search box, select filters, pagination, and distinct loading / error / empty
 * renderings. Later list screens (Play Archive, Playbook, Roster, Invoices) are
 * expected to reuse this rather than re-implement a `<table>`.
 *
 * The component is CONTROLLED: it renders the state it is handed and reports
 * intent through callbacks. That keeps the query in the page's URL, so a browser
 * refresh restores sort, filter, search and page.
 *
 * Responsive: below 640px the rows re-flow into stacked cards. Each cell carries
 * its column header in `data-label`, which the stylesheet renders as the label —
 * no duplicate markup, no separate mobile component.
 *
 * Desk mode (Play Archive behaviour) is switched on by passing `surface`:
 *  - compact toolbar: search + a "Sort and filter" popover (sort column,
 *    direction and every `filters` select) + a "Columns" picker whose choice
 *    is saved per user under `PUT /users/v1/layouts/user/table.<surface>`;
 *  - fit-to-width: the table never scrolls sideways — columns are dropped by
 *    `priority` (pinned ones never) and their values appear in an expandable
 *    row detail;
 *  - `primaryRowAction` + `rowMenu` give one visible action and a "⋯" menu.
 * Without `surface` the classic toolbar (labelled inline selects) renders, so
 * existing pages keep working until they opt in.
 */
import {
  Fragment,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { useIsPhone } from '../hooks/use-media-query';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../lib/constants';

import { ColumnPicker } from './column-picker';
import { EmptyState } from './empty-state';
import { Popover } from './popover';
import { RowMenu, type RowMenuItem } from './row-menu';
import { defaultPrefs, fitColumns, type FitColumn } from './table-layout';
import { useTableColumnPrefs } from './use-table-column-prefs';

export type { RowMenuItem } from './row-menu';

export type SortOrder = 'asc' | 'desc';

export interface DataTableColumn<Row> {
  id: string;
  header: string;
  render: (row: Row) => ReactNode;
  isSortable?: boolean;
  align?: 'start' | 'end';
  /** Hover / long-press explanation of what the column shows (`title` on the `<th>`). */
  headerTitle?: string;
  /**
   * Replace the header cell's content. Receives the default content (plain
   * text, or the sort button when the column is sortable) so a page can wrap
   * it — e.g. Play Archive adds a column-filter trigger next to each header.
   * `data-label` on the phone cards still uses the plain `header` string.
   */
  renderHeader?: (defaultHeader: ReactNode) => ReactNode;
  /* ---- desk mode (only read when the table has a `surface`) ---- */
  /** Lower = kept longer when the table is narrow. Default: column index. */
  priority?: number;
  /** Narrowest the column may render (px). Default 120. */
  minWidth?: number;
  /** Widest its content may claim before spare width is shared. */
  maxWidth?: number;
  /** Share of spare width. Default 1. */
  grow?: number;
  /** Shown until the user hides it in the Columns picker. Default true. */
  defaultVisible?: boolean;
  /** Never hidden or dropped. Default: the first column only. */
  isPinned?: boolean;
  /** Content for the row detail when the column is dropped. Default `render`. */
  renderDetail?: (row: Row) => ReactNode;
}

export interface DataTableSort {
  by: string;
  order: SortOrder;
}

export interface DataTableFilter {
  id: string;
  label: string;
  value: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
  /** The "no filter" value, for the active count and Reset (default: first option). */
  defaultValue?: string;
}

export interface DataTableSearch {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  /** Keystroke debounce before `onChange` fires, in ms. */
  debounceMs?: number;
}

export interface DataTablePagination {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export interface DataTableProps<Row> {
  /** Accessible table caption; also used to phrase the states. */
  caption: string;
  columns: readonly DataTableColumn<Row>[];
  rows: readonly Row[];
  getRowId: (row: Row) => string;
  sort?: DataTableSort;
  onSortChange?: (sort: DataTableSort) => void;
  search?: DataTableSearch;
  filters?: readonly DataTableFilter[];
  pagination?: DataTablePagination;
  /** Buttons rendered at the right of the toolbar (e.g. "Invite User"). */
  toolbarActions?: ReactNode;
  rowActions?: (row: Row) => ReactNode;
  rowActionsHeader?: string;
  /** Extra class on a row, e.g. to dim a removed member. */
  getRowClassName?: (row: Row) => string | undefined;
  isLoading?: boolean;
  /** Human-readable failure. Never pass a raw stack trace. */
  error?: string | null;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  /* ---- desk mode ---- */
  /**
   * Turns on desk mode (see file header). Short kebab-case name; the column
   * choice is saved as layout `table.<surface>`.
   */
  surface?: string;
  /** Human name for the saved layout. Default: caption + " columns". */
  layoutName?: string;
  /** One visible action per row (desk mode), rendered before the kebab. */
  primaryRowAction?: (row: Row) => ReactNode;
  /** Overflow menu items per row (desk mode). Empty list hides the kebab. */
  rowMenu?: (row: Row) => readonly RowMenuItem[];
  /** Accessible name of a row's kebab. Default "More actions". */
  getRowMenuLabel?: (row: Row) => string;
  /** Width reserved for the actions column in desk mode (px). */
  actionsWidth?: number;
}

/** Search input with its own draft state so typing stays responsive. */
function SearchBox({
  search,
  isCompact = false,
}: {
  search: DataTableSearch;
  isCompact?: boolean;
}): JSX.Element {
  const inputId = useId();
  const [draft, setDraft] = useState(search.value);

  // Adopt external changes (URL restore, "clear filters") without clobbering typing.
  useEffect(() => setDraft(search.value), [search.value]);

  useEffect(() => {
    if (draft === search.value) return undefined;
    const timer = setTimeout(() => search.onChange(draft), search.debounceMs ?? 250);
    return () => clearTimeout(timer);
  }, [draft, search]);

  return (
    <div className={`data-table__search${isCompact ? ' data-table__search--compact' : ''}`}>
      <label className={isCompact ? 'visually-hidden' : 'field__label'} htmlFor={inputId}>
        {search.label ?? 'Search'}
      </label>
      <input
        id={inputId}
        type="search"
        className="field__control"
        value={draft}
        placeholder={
          isCompact ? (search.placeholder ?? search.label ?? 'Search') : search.placeholder
        }
        onChange={(event) => setDraft(event.target.value)}
      />
    </div>
  );
}

function Pagination({ pagination }: { pagination: DataTablePagination }): JSX.Element {
  const { page, pageSize, total } = pagination;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const last = Math.min(total, page * pageSize);
  const sizeId = `page-size-${pageSize}`;

  return (
    <nav className="data-table__pagination" aria-label="Pagination">
      <p className="data-table__range" aria-live="polite">
        {total === 0 ? 'No results' : `Showing ${first}–${last} of ${total}`}
      </p>
      <div className="data-table__pager">
        {pagination.onPageSizeChange ? (
          <span className="data-table__page-size">
            <label className="muted" htmlFor={sizeId}>
              Rows
            </label>
            <select
              id={sizeId}
              className="field__control"
              value={pageSize}
              onChange={(event) => pagination.onPageSizeChange?.(Number(event.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </span>
        ) : null}
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => pagination.onPageChange(page - 1)}
          disabled={page <= 1}
        >
          Previous
        </button>
        <span className="data-table__page-indicator">
          Page {Math.min(page, totalPages)} of {totalPages}
        </span>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => pagination.onPageChange(page + 1)}
          disabled={page >= totalPages}
        >
          Next
        </button>
      </div>
    </nav>
  );
}

function renderHeaderContent(
  column: DataTableColumn<unknown>,
  defaultHeader: ReactNode
): ReactNode {
  return column.renderHeader ? column.renderHeader(defaultHeader) : defaultHeader;
}

function SortableHeader({
  column,
  sort,
  onSortChange,
}: {
  column: DataTableColumn<unknown>;
  sort: DataTableSort | undefined;
  onSortChange: (sort: DataTableSort) => void;
}): JSX.Element {
  const isActive = sort?.by === column.id;
  const nextOrder: SortOrder = isActive && sort?.order === 'asc' ? 'desc' : 'asc';
  // Explicit label: the `aria-sort` on the <th> already announces the current
  // direction, so the button's name states the ACTION it performs.
  const label = `Sort by ${column.header} ${nextOrder === 'asc' ? 'ascending' : 'descending'}`;
  return (
    <button
      type="button"
      className="data-table__sort"
      aria-label={label}
      onClick={() => onSortChange({ by: column.id, order: nextOrder })}
    >
      {column.header}
      <span aria-hidden="true" className="data-table__sort-icon">
        {isActive ? (sort?.order === 'asc' ? '▲' : '▼') : '↕'}
      </span>
    </button>
  );
}

function ClassicDataTable<Row>({
  caption,
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  search,
  filters,
  pagination,
  toolbarActions,
  rowActions,
  rowActionsHeader = 'Actions',
  getRowClassName,
  isLoading = false,
  error = null,
  onRetry,
  emptyTitle = 'Nothing to show yet',
  emptyDescription,
  emptyAction,
}: DataTableProps<Row>): JSX.Element {
  const hasToolbar =
    search !== undefined ||
    (filters?.length ?? 0) > 0 ||
    (toolbarActions !== undefined && toolbarActions !== null);
  const columnCount = columns.length + (rowActions ? 1 : 0);
  const skeletonRows = Math.min(pagination?.pageSize ?? DEFAULT_PAGE_SIZE, 5);
  /**
   * One state at a time. `loading` wins while a request is in flight (even
   * with zero rows, which is every first paint), so the empty state can only
   * appear once the load has actually settled.
   */
  const tableState: 'loading' | 'rows' | 'error' | 'empty' = isLoading
    ? 'loading'
    : rows.length > 0
      ? 'rows' // stale rows stay visible under an error banner on a failed refresh
      : error !== null
        ? 'error'
        : 'empty';

  return (
    <section className="data-table">
      {hasToolbar ? (
        <div className="data-table__toolbar">
          {search ? <SearchBox search={search} /> : null}
          {(filters ?? []).map((filter) => (
            <div className="data-table__filter" key={filter.id}>
              <label className="field__label" htmlFor={`filter-${filter.id}`}>
                {filter.label}
              </label>
              <select
                id={`filter-${filter.id}`}
                className="field__control"
                value={filter.value}
                onChange={(event) => filter.onChange(event.target.value)}
              >
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {toolbarActions ? <div className="data-table__actions">{toolbarActions}</div> : null}
        </div>
      ) : null}

      {error !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">We couldn’t load {caption.toLowerCase()}</p>
          <p>{error}</p>
          {onRetry ? (
            <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="data-table__scroll">
        <table className="data-table__table" aria-busy={isLoading || undefined}>
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  data-align={column.align ?? 'start'}
                  aria-sort={
                    sort?.by === column.id
                      ? sort.order === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : column.isSortable
                        ? 'none'
                        : undefined
                  }
                  title={column.headerTitle}
                >
                  {renderHeaderContent(
                    column as DataTableColumn<unknown>,
                    column.isSortable && onSortChange ? (
                      <SortableHeader
                        column={column as DataTableColumn<unknown>}
                        sort={sort}
                        onSortChange={onSortChange}
                      />
                    ) : (
                      column.header
                    )
                  )}
                </th>
              ))}
              {rowActions ? <th scope="col">{rowActionsHeader}</th> : null}
            </tr>
          </thead>
          <tbody>
            {/* Exactly one of: skeletons (loading) → rows (data) → nothing
                (settled + empty; the empty state below takes over). Skeletons
                and the empty state must never be on screen together. */}
            {tableState === 'loading'
              ? Array.from({ length: skeletonRows }, (_, index) => (
                  <tr
                    key={`skeleton-${index}`}
                    className="data-table__row--skeleton"
                    aria-hidden="true"
                    data-testid="data-table-skeleton-row"
                  >
                    {Array.from({ length: columnCount }, (__, cell) => (
                      <td key={cell}>
                        <span className="data-table__skeleton" />
                      </td>
                    ))}
                  </tr>
                ))
              : tableState === 'rows'
                ? rows.map((row) => (
                    <tr key={getRowId(row)} className={getRowClassName?.(row)}>
                      {columns.map((column) => (
                        <td
                          key={column.id}
                          data-label={column.header}
                          data-align={column.align ?? 'start'}
                        >
                          {column.render(row)}
                        </td>
                      ))}
                      {rowActions ? (
                        <td data-label={rowActionsHeader} className="data-table__row-actions">
                          {rowActions(row)}
                        </td>
                      ) : null}
                    </tr>
                  ))
                : null}
          </tbody>
        </table>
      </div>

      {tableState === 'empty' ? (
        <div className="data-table__empty" role="status" data-testid="data-table-empty">
          {/* The shared empty-state kit, so every table surface renders the
              same first-run treatment (Task 38 empty-state audit). */}
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            action={emptyAction}
            isCompact
          />
        </div>
      ) : null}

      {/* Screen-reader announcement for the skeleton phase (polite live region). */}
      {tableState === 'loading' ? (
        <p
          className="visually-hidden"
          role="status"
          aria-live="polite"
          data-testid="data-table-loading-announcement"
        >
          {`Loading ${caption}…`}
        </p>
      ) : null}

      {pagination ? <Pagination pagination={pagination} /> : null}
    </section>
  );
}

export function DataTable<Row>(props: DataTableProps<Row>): JSX.Element {
  return props.surface === undefined ? (
    <ClassicDataTable {...props} />
  ) : (
    <DeskDataTable {...props} />
  );
}

/* ===================================================================== desk */

const EXPANDER_PX = 32;
const DEFAULT_MIN_WIDTH = 120;

interface DeskColumn<Row> extends FitColumn {
  source: DataTableColumn<Row>;
  title: string;
}

function toDeskColumns<Row>(columns: readonly DataTableColumn<Row>[]): DeskColumn<Row>[] {
  return columns.map((column, index) => ({
    id: column.id,
    title: column.header,
    source: column,
    priority: column.priority ?? index,
    minWidth: column.minWidth ?? DEFAULT_MIN_WIDTH,
    maxWidth: column.maxWidth,
    grow: column.grow ?? 1,
    defaultVisible: column.defaultVisible ?? true,
    isPinned: column.isPinned ?? index === 0,
  }));
}

/** Width of an element, tracked with ResizeObserver (0 until measured / in jsdom). */
function useElementWidth(ref: RefObject<HTMLElement>): number {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const element = ref.current;
    if (element === null || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      const next = Math.floor(entries[0]?.contentRect.width ?? 0);
      setWidth((current) => (current === next ? current : next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}

function filterDefault(filter: DataTableFilter): string {
  return filter.defaultValue ?? filter.options[0]?.value ?? '';
}

function SortFilterPopover<Row>({
  columns,
  sort,
  onSortChange,
  filters,
}: {
  columns: readonly DataTableColumn<Row>[];
  sort: DataTableSort | undefined;
  onSortChange: ((sort: DataTableSort) => void) | undefined;
  filters: readonly DataTableFilter[];
}): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const baseId = useId();
  const sortable = columns.filter((column) => column.isSortable === true);
  const canSort = onSortChange !== undefined && sortable.length > 0;
  if (!canSort && filters.length === 0) return null;
  const activeFilters = filters.filter((filter) => filter.value !== filterDefault(filter)).length;
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="dt-tool"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        data-testid="sort-filter-trigger"
        onClick={() => setIsOpen((current) => !current)}
      >
        <span aria-hidden="true">⇅</span> Sort and filter
        {activeFilters > 0 ? (
          <span className="dt-tool__count" aria-label={`${activeFilters} active`}>
            {activeFilters}
          </span>
        ) : null}
      </button>
      <Popover
        anchorRef={triggerRef}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        label="Sort and filter"
        className="dt-sortfilter"
        width={300}
        testId="sort-filter"
      >
        <div className="dt-sortfilter__body">
          {canSort ? (
            <div className="dt-sortfilter__row">
              <label className="field__label" htmlFor={`${baseId}-sort`}>
                Sort by
              </label>
              <div className="dt-sortfilter__sort">
                <select
                  id={`${baseId}-sort`}
                  className="field__control"
                  value={sort?.by ?? ''}
                  onChange={(event) =>
                    onSortChange?.({ by: event.target.value, order: sort?.order ?? 'asc' })
                  }
                >
                  {sort === undefined ? <option value="">Default</option> : null}
                  {sortable.map((column) => (
                    <option key={column.id} value={column.id}>
                      {column.header}
                    </option>
                  ))}
                </select>
                <div className="dt-sortfilter__dir" role="group" aria-label="Sort direction">
                  {(['asc', 'desc'] as const).map((order) => (
                    <button
                      key={order}
                      type="button"
                      aria-pressed={sort?.order === order}
                      disabled={sort === undefined}
                      onClick={() => sort && onSortChange?.({ by: sort.by, order })}
                    >
                      {order === 'asc' ? 'Ascending' : 'Descending'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
          {filters.map((filter) => (
            <div className="dt-sortfilter__row" key={filter.id}>
              <label className="field__label" htmlFor={`filter-${filter.id}`}>
                {filter.label}
              </label>
              <select
                id={`filter-${filter.id}`}
                className="field__control"
                value={filter.value}
                onChange={(event) => filter.onChange(event.target.value)}
              >
                {filter.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div className="dt-sortfilter__foot">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={activeFilters === 0}
            onClick={() => {
              for (const filter of filters) {
                if (filter.value !== filterDefault(filter)) filter.onChange(filterDefault(filter));
              }
            }}
          >
            Reset filters
          </button>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => setIsOpen(false)}
          >
            Done
          </button>
        </div>
      </Popover>
    </>
  );
}

function DeskDataTable<Row>({
  caption,
  columns,
  rows,
  getRowId,
  sort,
  onSortChange,
  search,
  filters,
  pagination,
  toolbarActions,
  rowActions,
  rowActionsHeader = 'Actions',
  getRowClassName,
  isLoading = false,
  error = null,
  onRetry,
  emptyTitle = 'Nothing to show yet',
  emptyDescription,
  emptyAction,
  surface,
  layoutName,
  primaryRowAction,
  rowMenu,
  getRowMenuLabel,
  actionsWidth,
}: DataTableProps<Row>): JSX.Element {
  const deskColumns = useMemo(() => toDeskColumns(columns), [columns]);
  const { prefs, setPrefs, isSavedLocallyOnly } = useTableColumnPrefs(
    surface,
    deskColumns,
    layoutName ?? `${caption} columns`
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(scrollRef);
  const isPhone = useIsPhone();

  const hasActions =
    rowActions !== undefined || primaryRowAction !== undefined || rowMenu !== undefined;
  const actionsPx =
    actionsWidth ?? (rowActions !== undefined ? 200 : primaryRowAction !== undefined ? 150 : 48);

  const fit = useMemo(() => {
    // Unmeasured (first paint, jsdom) or phone cards: every visible column.
    if (width === 0 || isPhone) {
      const visible = new Set(prefs.columns.filter((entry) => entry.visible).map((e) => e.id));
      const shown = prefs.columns
        .filter((entry) => visible.has(entry.id))
        .map((entry) => deskColumns.find((column) => column.id === entry.id))
        .filter((column): column is DeskColumn<Row> => column !== undefined)
        .map((column) => ({ column, width: 0 }));
      return { shown, hidden: [] as DeskColumn<Row>[] };
    }
    const room = width - (hasActions ? actionsPx : 0);
    const first = fitColumns(deskColumns, prefs, { width: room, cellPadding: 0 });
    if (first.hidden.length === 0) return first;
    return fitColumns(deskColumns, prefs, { width: room - EXPANDER_PX, cellPadding: 0 });
  }, [width, isPhone, prefs, deskColumns, hasActions, actionsPx]);

  const shown = fit.shown;
  const hidden = fit.hidden;
  const hasExpander = hidden.length > 0;
  const hiddenIds = useMemo(() => new Set(hidden.map((column) => column.id)), [hidden]);
  const columnCount = shown.length + (hasExpander ? 1 : 0) + (hasActions ? 1 : 0);
  const skeletonRows = Math.min(pagination?.pageSize ?? DEFAULT_PAGE_SIZE, 5);
  const tableState: 'loading' | 'rows' | 'error' | 'empty' = isLoading
    ? 'loading'
    : rows.length > 0
      ? 'rows'
      : error !== null
        ? 'error'
        : 'empty';
  const pickerColumns = useMemo(
    () =>
      deskColumns.map((column) => ({
        id: column.id,
        title: column.title,
        isPinned: column.isPinned,
      })),
    [deskColumns]
  );
  const defaults = useMemo(() => defaultPrefs(deskColumns), [deskColumns]);
  const isMeasured = width > 0 && !isPhone;

  function toggleExpanded(id: string): void {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <section
      className={`data-table data-table--desk${prefs.density === 'comfortable' ? ' data-table--comfortable' : ''}`}
      data-surface={surface}
    >
      <div className="dt-toolbar">
        {search ? <SearchBox search={search} isCompact /> : null}
        <SortFilterPopover
          columns={columns}
          sort={sort}
          onSortChange={onSortChange}
          filters={filters ?? []}
        />
        {deskColumns.length > 1 ? (
          <ColumnPicker
            columns={pickerColumns}
            prefs={prefs}
            onChange={setPrefs}
            defaults={defaults}
            hiddenByWidth={hiddenIds}
            isSavedLocallyOnly={isSavedLocallyOnly}
            isOpen={isPickerOpen}
            onOpenChange={setIsPickerOpen}
            triggerClassName="dt-tool"
            countClassName="dt-tool__count"
          />
        ) : null}
        {toolbarActions ? <div className="dt-toolbar__end">{toolbarActions}</div> : null}
      </div>

      {error !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">We couldn’t load {caption.toLowerCase()}</p>
          <p>{error}</p>
          {onRetry ? (
            <button type="button" className="btn btn--secondary btn--sm" onClick={onRetry}>
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="data-table__scroll" ref={scrollRef}>
        <table
          className={`data-table__table${isMeasured ? ' data-table__table--fixed' : ''}`}
          aria-busy={isLoading || undefined}
        >
          <caption className="visually-hidden">{caption}</caption>
          {isMeasured ? (
            <colgroup>
              {hasExpander ? <col style={{ width: EXPANDER_PX }} /> : null}
              {shown.map(({ column, width: px }) => (
                <col key={column.id} style={{ width: px }} />
              ))}
              {hasActions ? <col style={{ width: actionsPx }} /> : null}
            </colgroup>
          ) : null}
          <thead>
            <tr>
              {hasExpander ? (
                <th scope="col" className="dt-expander-cell">
                  <button
                    type="button"
                    className="dt-more"
                    title={`${hidden.length} more ${hidden.length === 1 ? 'column' : 'columns'} — choose columns`}
                    onClick={() => setIsPickerOpen(true)}
                  >
                    +{hidden.length}
                  </button>
                </th>
              ) : null}
              {shown.map(({ column: desk }) => {
                const column = desk.source;
                return (
                  <th
                    key={column.id}
                    scope="col"
                    data-align={column.align ?? 'start'}
                    aria-sort={
                      sort?.by === column.id
                        ? sort.order === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : column.isSortable
                          ? 'none'
                          : undefined
                    }
                    title={column.headerTitle}
                  >
                    {renderHeaderContent(
                      column as DataTableColumn<unknown>,
                      column.isSortable && onSortChange ? (
                        <SortableHeader
                          column={column as DataTableColumn<unknown>}
                          sort={sort}
                          onSortChange={onSortChange}
                        />
                      ) : (
                        column.header
                      )
                    )}
                  </th>
                );
              })}
              {hasActions ? (
                <th scope="col" data-align="end" className="dt-actions-head">
                  <span className="visually-hidden">{rowActionsHeader}</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {tableState === 'loading'
              ? Array.from({ length: skeletonRows }, (_, index) => (
                  <tr
                    key={`skeleton-${index}`}
                    className="data-table__row--skeleton"
                    aria-hidden="true"
                    data-testid="data-table-skeleton-row"
                  >
                    {Array.from({ length: columnCount }, (__, cell) => (
                      <td key={cell}>
                        <span className="data-table__skeleton" />
                      </td>
                    ))}
                  </tr>
                ))
              : tableState === 'rows'
                ? rows.map((row) => {
                    const id = getRowId(row);
                    const isExpanded = hasExpander && expanded.has(id);
                    const menuItems = rowMenu?.(row) ?? [];
                    return (
                      <Fragment key={id}>
                        <tr
                          className={
                            [getRowClassName?.(row), isExpanded ? 'dt-row--expanded' : undefined]
                              .filter(Boolean)
                              .join(' ') || undefined
                          }
                        >
                          {hasExpander ? (
                            <td className="dt-expander-cell">
                              <button
                                type="button"
                                className="dt-expander"
                                aria-expanded={isExpanded}
                                aria-label={isExpanded ? 'Hide details' : 'Show details'}
                                onClick={() => toggleExpanded(id)}
                              >
                                <span aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                              </button>
                            </td>
                          ) : null}
                          {shown.map(({ column: desk }) => (
                            <td
                              key={desk.id}
                              data-label={desk.source.header}
                              data-align={desk.source.align ?? 'start'}
                            >
                              {desk.source.render(row)}
                            </td>
                          ))}
                          {hasActions ? (
                            <td
                              data-label={rowActionsHeader}
                              data-align="end"
                              className="data-table__row-actions dt-actions"
                            >
                              <div className="dt-actions__inner">
                                {rowActions ? rowActions(row) : null}
                                {primaryRowAction ? primaryRowAction(row) : null}
                                {menuItems.length > 0 ? (
                                  <RowMenu
                                    items={menuItems}
                                    label={getRowMenuLabel?.(row) ?? 'More actions'}
                                  />
                                ) : null}
                              </div>
                            </td>
                          ) : null}
                        </tr>
                        {isExpanded ? (
                          <tr className="dt-detail-row">
                            <td colSpan={columnCount}>
                              <dl className="dt-detail">
                                {hidden.map((desk) => (
                                  <div className="dt-detail__item" key={desk.id}>
                                    <dt>{desk.source.header}</dt>
                                    <dd>{(desk.source.renderDetail ?? desk.source.render)(row)}</dd>
                                  </div>
                                ))}
                              </dl>
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })
                : null}
          </tbody>
        </table>
      </div>

      {tableState === 'empty' ? (
        <div className="data-table__empty" role="status" data-testid="data-table-empty">
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            action={emptyAction}
            isCompact
          />
        </div>
      ) : null}

      {tableState === 'loading' ? (
        <p
          className="visually-hidden"
          role="status"
          aria-live="polite"
          data-testid="data-table-loading-announcement"
        >
          {`Loading ${caption}…`}
        </p>
      ) : null}

      {pagination ? <Pagination pagination={pagination} /> : null}
    </section>
  );
}
